import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, XAxis, YAxis } from "recharts";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { getTikTokDailyOrderSales, getTikTokShopPerformance } from "@/services/tiktokShop.service";
import { formatAmount } from "@/utils/formatters";

/** Fixed channel identities; never reassigned (see --chart-tiktok in index.css). */
const SHOPIFY_COLOR = "rgb(var(--brand-600))";
const TIKTOK_COLOR = "var(--chart-tiktok)";

/** "2026-09-12" → "Sep 12". */
function dayLabel(key: string) {
  const date = new Date(key);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Shopify affiliate sales and TikTok Shop sales, day by day on one USD axis.
 * The TikTok series comes from stored orders once the brand's data app
 * carries the order scope and the sync has run; until then it falls back to
 * the GMV TikTok's analytics report. The channel colors are fixed
 * identities (brand purple = Shopify, teal = TikTok) and the same pair is
 * used on every chart that compares channels. Both series follow the
 * dashboard's period toggle via `days`.
 */
export function SalesByChannelCard({
  shopifyByDay,
  days,
}: {
  /** The brand analytics daily revenue series the dashboard already loads. */
  shopifyByDay: Record<string, number> | undefined;
  days: number;
}) {
  const ordersQuery = useQuery({
    queryKey: ["tiktok-daily-order-sales", days],
    queryFn: () => getTikTokDailyOrderSales(days),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  // Orders cover the chart once synced; analytics GMV is the fallback.
  const useOrders = !!ordersQuery.data?.available && !ordersQuery.data.lastError;
  const tiktokQuery = useQuery({
    queryKey: ["tiktok-shop-performance", days],
    queryFn: () => getTikTokShopPerformance(days),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: ordersQuery.isFetched && !useOrders,
  });
  const tiktokLoading = ordersQuery.isLoading || (!useOrders && tiktokQuery.isLoading);
  const tiktokError = !useOrders && tiktokQuery.isError;

  const { data, shopifyTotal, tiktokTotal } = useMemo(() => {
    const tiktokByDay = new Map<string, number>();
    if (useOrders) {
      for (const day of ordersQuery.data?.days ?? []) tiktokByDay.set(day.date, day.sales);
    } else {
      for (const interval of tiktokQuery.data?.intervals ?? []) {
        tiktokByDay.set(interval.startDate, Number(interval.gmv?.amount ?? 0) || 0);
      }
    }
    const dates = new Set<string>([
      ...Object.keys(shopifyByDay ?? {}),
      ...tiktokByDay.keys(),
    ]);
    let shopifySum = 0;
    let tiktokSum = 0;
    const rows = [...dates].sort().map((date) => {
      const shopify = Number(shopifyByDay?.[date]) || 0;
      const tiktok = tiktokByDay.get(date) ?? 0;
      shopifySum += shopify;
      tiktokSum += tiktok;
      return { date, label: dayLabel(date), shopify, tiktok };
    });
    return { data: rows, shopifyTotal: shopifySum, tiktokTotal: tiktokSum };
  }, [shopifyByDay, tiktokQuery.data, ordersQuery.data, useOrders]);

  const total = shopifyTotal + tiktokTotal;
  const tiktokShare = total > 0 ? Math.round((tiktokTotal / total) * 100) : 0;

  return (
    <DashboardCard id="dashboard-sales-by-channel-card" title="Sales by channel">
      {tiktokError ? (
        <p className="px-5 pb-4 text-sm text-muted-foreground">
          TikTok Shop analytics could not be loaded, so only Shopify sales can be shown right now.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-5 pb-3">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="size-2 rounded-full" style={{ backgroundColor: SHOPIFY_COLOR }} />
              Shopify
              <span className="tabular-nums font-semibold text-foreground">{formatAmount(shopifyTotal)}</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="size-2 rounded-full" style={{ backgroundColor: TIKTOK_COLOR }} />
              TikTok Shop
              <span className="tabular-nums font-semibold text-foreground">
                {tiktokLoading ? "—" : formatAmount(tiktokTotal)}
              </span>
            </span>
            {total > 0 && !tiktokLoading && (
              <span className="text-xs text-muted-foreground">TikTok is {tiktokShare}% of tracked sales</span>
            )}
          </div>
          <div className="border-t border-border px-5 pb-4 pt-4">
            <ChartContainer
              config={{
                shopify: { label: "Shopify", color: SHOPIFY_COLOR },
                tiktok: { label: "TikTok Shop", color: TIKTOK_COLOR },
              }}
              className="h-44 w-full"
            >
              <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barGap={1}>
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  width={48}
                  tickFormatter={(v: number) => formatAmount(v)}
                />
                <ChartTooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  content={<ChartTooltipContent formatter={(value) => formatAmount(Number(value))} />}
                />
                <Bar dataKey="shopify" fill="var(--color-shopify)" radius={2} maxBarSize={14} />
                <Bar dataKey="tiktok" fill="var(--color-tiktok)" radius={2} maxBarSize={14} />
              </BarChart>
            </ChartContainer>
          </div>
          <p className="px-5 pb-3 text-xs text-muted-foreground">
            {useOrders ? (
              <>
                Shopify shows affiliate-attributed sales; TikTok Shop shows product sales from your TikTok orders
                (paid, not cancelled, samples excluded)
                {ordersQuery.data?.backfill && !ordersQuery.data.backfill.complete
                  ? ", still loading the last 90 days"
                  : ordersQuery.data?.syncedThrough
                    ? ` (synced ${new Date(ordersQuery.data.syncedThrough).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })})`
                    : ""}
                .
                {ordersQuery.data?.dedupedOrders
                  ? ` ${ordersQuery.data.dedupedOrders} TikTok order${ordersQuery.data.dedupedOrders === 1 ? "" : "s"} already counted on the Shopify side ${ordersQuery.data.dedupedOrders === 1 ? "was" : "were"} left out.`
                  : " TikTok pays creator commissions on these itself; they never enter Reveshare payouts."}
              </>
            ) : (
              <>
                Shopify shows affiliate-attributed sales; TikTok Shop shows the shop's GMV as TikTok reports it
                {tiktokQuery.data?.latestAvailableDate ? ` (through ${tiktokQuery.data.latestAvailableDate})` : ""}.
                {ordersQuery.data?.reason === "SCOPE_MISSING"
                  ? " Reconnect TikTok Shop sales data on the Integrations page to chart your actual orders instead."
                  : " The two are tracked differently, so the split is a guide, not an exact ledger."}
              </>
            )}
          </p>
        </>
      )}
    </DashboardCard>
  );
}
